import type { ResumeData, RoleItem, SectionItem } from "@/schema/resume/data";

import type { AiEditResult, AiEditTarget, FieldSuggestion } from "./section-editing";

import { createEffectiveProposalRegenerationDraft } from "./section-editing";

export type AiEditUrlDraft = {
  url: string;
  label: string;
};

export type AiEditRoleDraft = {
  id: string;
  position: string;
  period: string;
  description: string;
};

export type AiEditDraft =
  | {
      type: "summary";
      content: string;
    }
  | {
      type: "experience";
      id: string;
      hidden: boolean;
      options: { showLinkInTitle: boolean };
      company: string;
      location: string;
      position: string;
      period: string;
      website: AiEditUrlDraft;
      description: string;
      roles: AiEditRoleDraft[];
    }
  | {
      type: "project";
      id: string;
      hidden: boolean;
      options: { showLinkInTitle: boolean };
      name: string;
      period: string;
      website: AiEditUrlDraft;
      description: string;
    };

export type AiEditOriginalFieldId = "position" | "company" | "period" | "location" | "projectName" | "website";

export type AiEditOriginalField = {
  id: AiEditOriginalFieldId;
  value: string;
  protected: boolean;
};

export type AiEditUiTargetSnapshot = {
  target: AiEditTarget;
  original: AiEditDraft;
  originalFields: AiEditOriginalField[];
};

export const emptyAiEditResult: AiEditResult = {
  contentHtml: "",
  descriptionHtml: "",
  position: "",
  name: "",
  roles: [],
};

export const emptyAiEditDraft: AiEditDraft = {
  type: "summary",
  content: "",
};

function textValue(value: string | undefined) {
  return value?.trim() ?? "";
}

function filterOriginalFields(fields: AiEditOriginalField[]) {
  return fields.filter((field) => textValue(field.value).length > 0);
}

function cloneUrl(value: AiEditUrlDraft | undefined): AiEditUrlDraft {
  return {
    url: value?.url ?? "",
    label: value?.label ?? "",
  };
}

function cloneOptions(value: { showLinkInTitle?: boolean } | undefined) {
  return {
    showLinkInTitle: value?.showLinkInTitle ?? false,
  };
}

function cloneRole(role: RoleItem): AiEditRoleDraft {
  return {
    id: role.id,
    position: role.position,
    period: role.period,
    description: role.description,
  };
}

function createExperienceDraft(item: SectionItem<"experience">): AiEditDraft {
  return {
    type: "experience",
    id: item.id,
    hidden: item.hidden,
    options: cloneOptions(item.options),
    company: item.company,
    location: item.location,
    position: item.position,
    period: item.period,
    website: cloneUrl(item.website),
    description: item.description,
    roles: item.roles.map(cloneRole),
  };
}

function createProjectDraft(item: SectionItem<"projects">): AiEditDraft {
  return {
    type: "project",
    id: item.id,
    hidden: item.hidden,
    options: cloneOptions(item.options),
    name: item.name,
    period: item.period,
    website: cloneUrl(item.website),
    description: item.description,
  };
}

export function getAiEditTargetKey(target: AiEditTarget) {
  return "itemId" in target ? `${target.type}:${target.itemId}` : target.type;
}

export function getAiEditUiTargetSnapshot(data: ResumeData, target: AiEditTarget): AiEditUiTargetSnapshot {
  if (target.type === "summary") {
    return {
      target,
      original: { type: "summary", content: data.summary.content },
      originalFields: [],
    };
  }

  if (target.type === "experience") {
    const item = data.sections.experience.items.find((entry) => entry.id === target.itemId);
    if (!item) throw new Error("Experience item was not found.");
    const originalFields = filterOriginalFields([
      { id: "position", value: item.position, protected: false },
      { id: "company", value: item.company, protected: true },
      { id: "period", value: item.period, protected: true },
      { id: "location", value: item.location, protected: true },
      {
        id: "website",
        value: item.website.label || item.website.url,
        protected: true,
      },
    ]);

    return {
      target,
      original: createExperienceDraft(item),
      originalFields,
    };
  }

  const item = data.sections.projects.items.find((entry) => entry.id === target.itemId);
  if (!item) throw new Error("Project item was not found.");
  const originalFields = filterOriginalFields([
    { id: "projectName", value: item.name, protected: false },
    { id: "period", value: item.period, protected: true },
    { id: "website", value: item.website.label || item.website.url, protected: true },
  ]);

  return {
    target,
    original: createProjectDraft(item),
    originalFields,
  };
}

export function getAiEditDraftMainHtml(draft: AiEditDraft) {
  if (draft.type === "summary") return draft.content;
  return draft.description;
}

export function setAiEditDraftMainHtml(draft: AiEditDraft, value: string): AiEditDraft {
  if (draft.type === "summary") return { ...draft, content: value };
  return { ...draft, description: value };
}

export function toAiEditResult(draft: AiEditDraft): AiEditResult {
  if (draft.type === "summary") {
    return { ...emptyAiEditResult, contentHtml: draft.content };
  }

  if (draft.type === "experience") {
    return {
      ...emptyAiEditResult,
      descriptionHtml: draft.roles.length > 0 ? "" : draft.description,
      position: draft.position,
      roles: draft.roles.map((role) => ({
        id: role.id,
        position: role.position,
        descriptionHtml: role.description,
      })),
    };
  }

  return {
    ...emptyAiEditResult,
    descriptionHtml: draft.description,
    name: draft.name,
  };
}

export function applyFieldSuggestionToAiEditResult(result: AiEditResult, suggestion: FieldSuggestion) {
  if (suggestion.field !== "position" && suggestion.field !== "name") return result;
  return { ...result, [suggestion.field]: suggestion.suggestedValue };
}

function hasHtmlText(value: string) {
  return textValue(value.replace(/<[^>]*>/g, " ")).length > 0;
}

export function mergeAiEditResultIntoDraft(draft: AiEditDraft, result: AiEditResult): AiEditDraft {
  const target: AiEditTarget = draft.type === "summary" ? { type: "summary" } : { type: draft.type, itemId: draft.id };
  const effective = createEffectiveProposalRegenerationDraft(target, toAiEditResult(draft), result);
  if (draft.type === "summary") {
    return { ...draft, content: effective.contentHtml };
  }

  if (draft.type === "experience") {
    return {
      ...draft,
      position: effective.position,
      description: effective.descriptionHtml,
      roles: draft.roles.map((role) => {
        const generatedRole = effective.roles.find((entry) => entry.id === role.id);
        if (!generatedRole) return role;

        return {
          ...role,
          position: generatedRole.position,
          description: generatedRole.descriptionHtml,
        };
      }),
    };
  }

  return {
    ...draft,
    name: effective.name,
    description: effective.descriptionHtml,
  };
}

export function createAiEditTuneDraft(draft: AiEditDraft): AiEditDraft {
  if (draft.type === "summary") {
    return { ...draft, content: "" };
  }

  if (draft.type === "experience") {
    return {
      ...draft,
      position: "",
      description: "",
      roles: draft.roles.map((role) => ({
        ...role,
        position: "",
        description: "",
      })),
    };
  }

  return {
    ...draft,
    name: "",
    description: "",
  };
}

export function applyAiEditDraftToResumeData(data: ResumeData, target: AiEditTarget, draft: AiEditDraft) {
  if (target.type === "summary") {
    if (draft.type !== "summary") return false;
    data.summary.content = draft.content;
    return true;
  }

  if (target.type === "experience") {
    if (draft.type !== "experience") return false;
    const item = data.sections.experience.items.find((entry) => entry.id === target.itemId);
    if (!item) return false;

    item.hidden = draft.hidden;
    item.options = draft.options;
    item.company = draft.company;
    item.position = draft.position;
    item.location = draft.location;
    item.period = draft.period;
    item.website = draft.website;
    item.description = draft.description;
    item.roles = draft.roles.map((role) => ({ ...role }));
    return true;
  }

  if (draft.type !== "project") return false;
  const item = data.sections.projects.items.find((entry) => entry.id === target.itemId);
  if (!item) return false;

  item.hidden = draft.hidden;
  item.options = draft.options;
  item.name = draft.name;
  item.period = draft.period;
  item.website = draft.website;
  item.description = draft.description;
  return true;
}

export function applyAiEditResultToResumeData(data: ResumeData, target: AiEditTarget, result: AiEditResult) {
  const snapshot = getAiEditUiTargetSnapshot(data, target);
  const draft = mergeAiEditResultIntoDraft(snapshot.original, result);
  return applyAiEditDraftToResumeData(data, target, draft);
}

export function createResumeDataWithAiEditDraft(data: ResumeData, target: AiEditTarget, draft: AiEditDraft) {
  const nextData = structuredClone(data);
  applyAiEditDraftToResumeData(nextData, target, draft);
  return nextData;
}

export function hasAiEditDraftRequiredContent(draft: AiEditDraft) {
  if (draft.type === "summary") return hasHtmlText(draft.content);
  if (draft.type === "experience") return draft.company.trim().length > 0;
  return draft.name.trim().length > 0;
}

export function hasAiEditDraftAiEligibleContent(draft: AiEditDraft) {
  if (draft.type === "summary") return hasHtmlText(draft.content);

  if (draft.type === "experience") {
    if (draft.position.trim().length > 0) return true;
    if (hasHtmlText(draft.description)) return true;

    return draft.roles.some((role) => role.position.trim().length > 0 || hasHtmlText(role.description));
  }

  return draft.name.trim().length > 0 || hasHtmlText(draft.description);
}
